import { makeStyles, tokens } from '@fluentui/react-components';
import { LookupRecordContext, RecordContext, useT } from '@powerportalspro/react';
import {
  ChoiceEdit,
  ChoiceEditorType,
  ColumnEdit,
  DeleteRecordGridButton,
  GridToolbarItems,
  ImageEdit,
  LinkExistingRecordGridButton,
  LookupEdit,
  ManyToManyLookupEdit,
  MenuBar,
  MoneyEdit,
  NewRecordGridButton,
  OpenRecordGridButton,
  RefreshContextButton,
  SaveContextButton,
  Section,
  SectionColumn,
  SubGrid,
  TabPanel,
  Tabs,
  TextEdit,
  UnlinkExistingRecordGridButton,
  ValidationSummary,
  WizardRecordPage,
} from '@powerportalspro/react-fluent';

// Most of the major components working together on one account: column editors, a lookup with
// the primary contact's own columns edited beside it, a many-to-many picker, and the account's
// regions and contacts in SubGrids. One Save commits the account, its primary contact, and every
// row queued in the grids. The account comes from the `accountId` query parameter; without one
// the page creates a new account.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.MyAccountDetails.MyAccountComponent';
const EDIT_CONTACT_STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Grids.SubGrid.EditContactForm';

const CONTACT_LOOKUP_VIEWS = { contact: ['0d5d377b-5e7c-47b5-bab1-a5cb8b4ac105'] };

const ACTIVE_REGIONS_VIEW_ID = 'f8f790bc-7277-45c1-b1fd-c1248a766279';
const REGION_VIEW_IDS = [ACTIVE_REGIONS_VIEW_ID, '0c4a3a0d-28a3-4f6f-bbd7-a7f01a7faed0'];

const CONTACT_GRID_DEFAULT_VIEW_ID = '00000000-0000-0000-00aa-000010001003';
const CONTACT_GRID_VIEW_IDS = [
  '00000000-0000-0000-00aa-000010001004',
  '0d5d377b-5e7c-47b5-bab1-a5cb8b4ac105',
  CONTACT_GRID_DEFAULT_VIEW_ID,
  '00000000-0000-0000-00aa-000010001033',
];

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  tabs: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
  formTab: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
});

export function MyAccountMainSample() {
  const styles = useStyles();
  const t = useT();

  return (
    <RecordContext
      table="account"
      queryParameterName="accountId"
      forceSuccessfulValidationBeforeSave
    >
      <div className={styles.root}>
        <MenuBar>
          <SaveContextButton />
          <RefreshContextButton />
        </MenuBar>
        <ValidationSummary />

        <Section borderVisible={false} columnCount={3}>
          <SectionColumn headerText={t(`${STRINGS_BASE}.section-account-info`)}>
            <ColumnEdit columnName="name" />
            <ColumnEdit columnName="numberofemployees" />
            <ColumnEdit columnName="address1_latitude" />
            <ColumnEdit columnName="address1_longitude" />
            <ChoiceEdit columnName="territorycode" editorType={ChoiceEditorType.RadioButtons} />
            <LookupEdit columnName="primarycontactid" viewIds={CONTACT_LOOKUP_VIEWS} />
            <ColumnEdit columnName="createdon" />
            <ManyToManyLookupEdit
              relationshipName="ppp_Account_ppp_Region_ppp_Region"
              minimumNumberOfItems={1}
              maximumNumberOfItems={3}
              viewIds={REGION_VIEW_IDS}
              defaultViewId={ACTIVE_REGIONS_VIEW_ID}
            />
          </SectionColumn>
          <SectionColumn headerText={t(`${STRINGS_BASE}.section-contact-info`)}>
            {/* The primary contact's own columns, saved with the account. */}
            <LookupRecordContext columnName="primarycontactid">
              <ColumnEdit columnName="firstname" />
              <ColumnEdit columnName="lastname" />
              <ColumnEdit columnName="emailaddress1" />
              <ColumnEdit columnName="ppp_contract" />
              <ColumnEdit columnName="ppp_personalinterests" style={{ maxHeight: '150px' }} />
              <ImageEdit columnName="ppp_profileimage" displayImage />
            </LookupRecordContext>
          </SectionColumn>
        </Section>

        <Section childContentStyle={{ padding: 0 }}>
          <SectionColumn borderVisible={false} style={{ padding: 0 }}>
            {/* The selected tab is kept in ?tab=. Both tabs are grids, so each loads when its tab
                is first opened, and stays, keeping its queued rows while the other tab shows. */}
            <Tabs selectedTabQueryParameter="tab" className={styles.tabs}>
              <TabPanel value="regions" header={t(`${STRINGS_BASE}.tab-regions`)}>
                <SubGrid
                  relationshipName="ppp_Account_ppp_Region_ppp_Region"
                  minHeight="400px"
                  maxHeight="400px"
                  pageSize={10}
                  title={t(`${STRINGS_BASE}.tab-regions`)}
                  allowEdit
                  defaultViewId={ACTIVE_REGIONS_VIEW_ID}
                >
                  <GridToolbarItems>
                    <LinkExistingRecordGridButton />
                    <UnlinkExistingRecordGridButton />
                  </GridToolbarItems>
                </SubGrid>
              </TabPanel>

              <TabPanel value="contacts" header={t(`${STRINGS_BASE}.tab-contacts`)}>
                <SubGrid
                  relationshipName="contact_customer_accounts"
                  minHeight="400px"
                  maxHeight="400px"
                  pageSize={10}
                  title={t(`${STRINGS_BASE}.tab-contacts`)}
                  viewIds={CONTACT_GRID_VIEW_IDS}
                  defaultViewId={CONTACT_GRID_DEFAULT_VIEW_ID}
                >
                  <GridToolbarItems>
                    {/* A WizardRecordPage child makes the New button a step-by-step wizard. */}
                    <NewRecordGridButton location="center">
                      <WizardRecordPage>
                        <TextEdit columnName="firstname" />
                        <TextEdit columnName="middlename" />
                        <TextEdit columnName="lastname" />
                      </WizardRecordPage>
                      <WizardRecordPage>
                        <MoneyEdit columnName="annualincome" />
                        <TextEdit columnName="telephone1" type="tel" />
                      </WizardRecordPage>
                    </NewRecordGridButton>
                    <OpenRecordGridButton location="panel">
                      <EditContactForm />
                    </OpenRecordGridButton>
                    <DeleteRecordGridButton mode="individually" />
                  </GridToolbarItems>
                </SubGrid>
              </TabPanel>
            </Tabs>
          </SectionColumn>
        </Section>
      </div>
    </RecordContext>
  );
}

// The contact form the Open button shows in a side panel, in two tabs. Both mount with the form
// (lazyMount={false}), so it validates and saves them unopened and an edit on one tab survives
// switching to the other.
function EditContactForm() {
  const styles = useStyles();
  const t = useT();

  return (
    <Tabs lazyMount={false}>
      <TabPanel value="general" header={t(`${EDIT_CONTACT_STRINGS_BASE}.tab1.title`)}>
        <div className={styles.formTab}>
          <TextEdit columnName="firstname" />
          <TextEdit columnName="middlename" />
          <TextEdit columnName="lastname" />
        </div>
      </TabPanel>
      <TabPanel value="other" header={t(`${EDIT_CONTACT_STRINGS_BASE}.tab2.title`)}>
        <div className={styles.formTab}>
          <MoneyEdit columnName="annualincome" />
          <TextEdit columnName="telephone1" type="tel" />
        </div>
      </TabPanel>
    </Tabs>
  );
}
